import React, { useState, useEffect, useRef } from "react";
import usePageTitle from '../../../hooks/usePageTitle';
import { useDispatch } from "react-redux";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import http from "../../../http-common";
import { useTranslation } from "react-i18next";
import AuthLeftPanel from "./AuthLeftPanel";
import { useTheme } from "../../../context/ThemeContext";
import { useSocket } from "../../../context/SocketProvider";

function LoginForm() {
  usePageTitle('Login');
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // Set by AuthRoutes when it bounced a logged-out user here. Only same-site
  // paths: "//host" is protocol-relative, so it is rejected.
  const from = location.state?.from;
  const afterLogin =
    typeof from === "string" && from.startsWith("/") && !from.startsWith("//") ? from : "/";
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const emailInputRef = useRef(null);
  const socket = useSocket();

  useEffect(() => {
    emailInputRef.current.focus();
  }, []);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    if (id === "email") setEmail(value.trim());
    if (id === "password") setPassword(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError(t("Please provide both email and password."));
      return;
    }
    try {
      const { data } = await http.post("/users/login", { email, password });

      http.put(`/users/lastactive/${data.id}`).catch(() => {});

      if (data.user && !data.isVerified) {
        navigate("/verification", { state: { user: data.user } });
        return;
      }

      setError("");
      dispatch({ type: "LOGIN", payload: data });

      // The shared socket (SocketProvider) connects itself once the token above
      // lands in the store; nudge it here too so the connection doesn't wait for
      // the next render/reconnect cycle.
      if (socket && !socket.connected) socket.connect();

      navigate(afterLogin);
    } catch (err) {
      setError(err.response?.data?.message ?? t("Login failed"));
    }
  };

  return (
    <div className="tw-flex tw-min-h-screen">
      <AuthLeftPanel />

      <div className={`tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-px-6 tw-pt-8 tw-pb-24 tw-overflow-y-auto tw-transition-colors tw-duration-300 ${
        isLight ? 'tw-bg-[#F4F0FA]' : 'tw-bg-gradient-to-b tw-from-[#09010f] tw-to-[#1E0039]'
      }`}>
        <div className="tw-w-full tw-max-w-sm">

          <div className="tw-mb-6">
            <h2 className={`tw-text-2xl tw-font-bold ${isLight ? 'tw-text-[#1E0039]' : 'tw-text-white'}`}>{t("Welcome back")}</h2>
            <p className={`tw-mt-1 tw-text-sm ${isLight ? 'tw-text-gray-500' : 'tw-text-gray-400'}`}>{t("Sign in to your account")}</p>
          </div>

          <div className={`tw-rounded-2xl tw-border tw-p-8 tw-shadow-[0_0_40px_rgba(255,67,167,0.08)] ${
            isLight ? 'tw-border-gray-200 tw-bg-white' : 'tw-border-[#FF43A7]/20 tw-bg-white/5'
          }`}>
            <form onSubmit={handleSubmit} className="tw-flex tw-flex-col tw-gap-4">
              <input
                type="email"
                id="email"
                value={email}
                onChange={handleInputChange}
                placeholder={t("Email")}
                ref={emailInputRef}
                className={`tw-w-full tw-rounded-lg tw-border tw-px-4 tw-py-3 tw-text-base tw-outline-none tw-transition-colors focus:tw-border-[#FF43A7]/60 ${
                  isLight
                    ? 'tw-border-gray-200 tw-bg-white tw-text-[#1E0039] tw-placeholder-[#9ca3af]'
                    : 'tw-border-white/10 tw-bg-white/5 tw-text-white tw-placeholder-gray-500'
                }`}
              />

              <div className="tw-relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={password}
                  onChange={handleInputChange}
                  placeholder={t("Password")}
                  className={`tw-w-full tw-rounded-lg tw-border tw-px-4 tw-py-3 tw-pr-12 tw-text-base tw-outline-none tw-transition-colors focus:tw-border-[#FF43A7]/60 ${
                    isLight
                      ? 'tw-border-gray-200 tw-bg-white tw-text-[#1E0039] tw-placeholder-[#9ca3af]'
                      : 'tw-border-white/10 tw-bg-white/5 tw-text-white tw-placeholder-gray-500'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className={`tw-absolute tw-inset-y-0 tw-right-0 tw-flex tw-w-12 tw-cursor-pointer tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-transition-colors ${
                    isLight ? 'tw-text-gray-400 hover:tw-text-[#1E0039]' : 'tw-text-gray-500 hover:tw-text-gray-300'
                  }`}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
              </div>

              {error && (
                <p className="tw-text-center tw-text-sm tw-text-red-400">*{error}</p>
              )}

              <button
                type="submit"
                className="tw-mt-1 tw-w-full tw-cursor-pointer tw-rounded-full tw-border-0 tw-bg-[#FF43A7] tw-py-3 tw-text-base tw-font-semibold tw-text-white tw-shadow-[0_0_20px_rgba(255,67,167,0.3)] tw-transition-all hover:tw-bg-[#FF43A7]/80"
              >
                {t("Sign In")}
              </button>

              <div className={`tw-mt-1 tw-flex tw-flex-col tw-gap-2 tw-text-center tw-text-sm ${isLight ? 'tw-text-gray-500' : 'tw-text-gray-400'}`}>
                <p>
                  {t("Don't have an account yet?")}{" "}
                  <Link to="/signup" className="tw-text-[#FF43A7] hover:tw-underline">
                    {t("Register")}
                  </Link>
                </p>
                <Link to="/sendresetpasswordlink" className="tw-text-[#FF43A7] hover:tw-underline">
                  {t("Forgot password?")}
                </Link>
              </div>
            </form>
          </div>

        </div>
      </div>
    </div>
  );
}

export default LoginForm;
